import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApiError, Business } from '../../types';
import {
  businessService,
  CreateBusinessRequest,
  UpdateBusinessRequest,
} from '../../services';

/**
 * Business profile store for the buyer's currently-active Business.
 * `business` is `null` for Individual buyers.
 */
export class BusinessStore {
  business: Business | null = null;
  loading = false;
  saving = false;
  error: ApiError | null = null;

  constructor() {
    makeObservable(this, {
      business: observable,
      loading: observable,
      saving: observable,
      error: observable,
      isOwnedBusinessActive: computed,
      load: action,
      create: action,
      update: action,
      clear: action,
    });
  }

  get isOwnedBusinessActive(): boolean {
    return this.business?.status === 'ACTIVE';
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const res = await businessService.get();
      runInAction(() => {
        this.business = res;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async create(req: CreateBusinessRequest): Promise<Business> {
    this.saving = true;
    this.error = null;
    try {
      const next = await businessService.create(req);
      runInAction(() => {
        this.business = next;
      });
      return next;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async update(patch: UpdateBusinessRequest): Promise<Business> {
    this.saving = true;
    this.error = null;
    try {
      const next = await businessService.update(patch);
      runInAction(() => {
        this.business = next;
      });
      return next;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  clear(): void {
    this.business = null;
    this.error = null;
  }
}
